/**
 * <package-product-form>
 *
 * Handles add-to-cart for configurable "package" products: the main product is added
 * at its own price, plus one additional cart line per selected add-on/component product
 * (each a real Shopify product/variant, so Shopify handles its own inventory).
 *
 * This is a separate custom element from the theme's own <product-form> so that normal,
 * non-package products keep using the theme's existing add-to-cart handling completely
 * unchanged, and so the two submit handlers can never both fire on the same form.
 *
 * Markup contract (rendered by sections/main-product.liquid + snippets/package-options.liquid):
 * - A <form data-ajax="true" class="product-purchase-form js-product-form"> with a hidden
 *   input[name="id"] (main product variant) and, optionally, an input[name="quantity"].
 * - Zero or more <select data-package-option-select data-group-name="..."> elements, whose
 *   selected <option> carries data-variant-id, data-available="true|false", data-price and
 *   data-compare-price (variant price / compare-at price in cents; 0 when not set).
 * - A sibling [data-package-price] price container (the section's "price" block) carrying
 *   data-base-price and data-base-compare-price (cents) and data-use-currency-code, whose
 *   .price element is kept in sync with the base price plus the currently selected add-ons.
 *   A [data-package-was-price] element inside the same container (if present) is kept in sync
 *   with the combined compare-at price and shown/hidden depending on whether it's above price.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('package-product-form')) {
    return;
  }

  function generatePackageId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'pkg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  var PackageProductForm = class extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form[data-ajax="true"].product-purchase-form');
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
      this.addEventListener('change', this.handleOptionChange.bind(this));
      this.updatePriceDisplay();
    }

    /**
     * Reads the currently selected add-on for each option group.
     * @returns {Array<{groupName: string, variantId: string|null, available: boolean, price: number}>}
     */
    getSelectedComponents() {
      var components = [];
      var selects = this.querySelectorAll('[data-package-option-select]');

      for (var i = 0; i < selects.length; i++) {
        var select = selects[i];
        var option = select.options[select.selectedIndex];

        if (!option || !option.value) {
          continue;
        }

        components.push({
          groupName: select.getAttribute('data-group-name') || '',
          variantId: option.getAttribute('data-variant-id'),
          available: option.getAttribute('data-available') === 'true',
          price: parseInt(option.getAttribute('data-price'), 10) || 0,
          comparePrice: parseInt(option.getAttribute('data-compare-price'), 10) || 0
        });
      }

      return components;
    }

    handleOptionChange(evt) {
      if (evt.target && evt.target.matches && evt.target.matches('[data-package-option-select]')) {
        this.updatePriceDisplay();
      }
    }

    /**
     * Recomputes the base price plus the currently selected add-ons and writes it into the
     * section's price block, so upgrade choices are reflected before the customer adds to cart.
     */
    updatePriceDisplay() {
      var section = this.closest('[data-section-type="product-template"]');
      if (!section) {
        return;
      }

      var priceContainer = section.querySelector('[data-package-price]');
      if (!priceContainer) {
        return;
      }

      var basePrice = parseInt(priceContainer.getAttribute('data-base-price'), 10);
      if (isNaN(basePrice)) {
        return;
      }

      if (!theme.Shopify || typeof theme.Shopify.formatMoney !== 'function') {
        return;
      }

      var priceEl = priceContainer.querySelector('.price');
      if (!priceEl) {
        return;
      }

      var baseComparePrice = parseInt(priceContainer.getAttribute('data-base-compare-price'), 10) || 0;

      var total = basePrice;
      // Was-price contribution: a component's own compare-at price when it's on sale,
      // otherwise its regular price, so the "was" total always rises in step with the
      // "now" total even for add-ons that aren't themselves discounted.
      var totalWas = baseComparePrice > basePrice ? baseComparePrice : basePrice;

      this.getSelectedComponents().forEach(function (component) {
        total += component.price;
        totalWas += component.comparePrice > component.price ? component.comparePrice : component.price;
      });

      var useCurrencyCode = priceContainer.getAttribute('data-use-currency-code') === 'true';
      var format = useCurrencyCode ? theme.money_format_with_product_code_preference : theme.money_format;

      priceEl.textContent = theme.Shopify.formatMoney(total, format);

      var onSale = totalWas > total;
      priceEl.classList.toggle('price--reduced', onSale);

      var wasPriceEl = priceContainer.querySelector('[data-package-was-price]');
      if (wasPriceEl) {
        if (onSale) {
          wasPriceEl.textContent = theme.Shopify.formatMoney(totalWas, theme.money_format);
          wasPriceEl.style.display = '';
        } else {
          wasPriceEl.style.display = 'none';
        }
      }
    }

    handleSubmit(evt) {
      evt.preventDefault();

      var form = this.form;
      var submitBtn = form.querySelector('button[type="submit"]');
      var idInput = form.querySelector('input[name="id"]');
      var quantityInput = form.querySelector('input[name="quantity"]');

      if (!idInput || !idInput.value) {
        return;
      }

      var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
      if (!quantity || quantity < 1) {
        quantity = 1;
      }

      var components = this.getSelectedComponents();
      var hasUnavailableComponent = components.some(function (component) {
        return !component.available;
      });

      if (hasUnavailableComponent) {
        theme.showQuickPopup(theme.strings.products_package_component_unavailable, submitBtn || form, 'error');
        return;
      }

      var packageId = generatePackageId();
      var packageName = this.getAttribute('data-package-name') || '';

      var items = [{
        id: parseInt(idInput.value, 10),
        quantity: quantity,
        properties: {
          '_package_id': packageId,
          '_package_role': 'main'
        }
      }];

      components.forEach(function (component) {
        if (!component.variantId) {
          return;
        }

        items.push({
          id: parseInt(component.variantId, 10),
          quantity: quantity,
          properties: {
            '_package_id': packageId,
            '_package_role': 'component',
            '_package_group': component.groupName,
            '_package_parent': packageName
          }
        });
      });

      if (submitBtn) {
        submitBtn.setAttribute('disabled', 'disabled');
        submitBtn.innerHTML = theme.strings.products_product_adding_to_cart;
      }

      fetch(theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: items })
      }).
      then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw data;
          }
          return data;
        });
      }).
      then(function () {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          var addedHtml = (theme.icons && theme.icons.tick ? theme.icons.tick + ' ' : '') + theme.strings.products_product_added_to_cart;
          submitBtn.innerHTML = addedHtml;

          setTimeout(function () {
            if (submitBtn.innerHTML === addedHtml) {
              submitBtn.innerHTML = theme.strings.products_product_add_to_cart;
            }
          }, 2500);
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
        );

        if (form.getAttribute('data-ajax-redirect') === 'true') {
          window.location = theme.routes.cart_url;
        }
      }).
      catch(function (error) {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.innerHTML = theme.strings.products_product_add_to_cart;
        }

        var message = theme.strings.products_package_add_error;
        if (error && typeof error.description === 'string') {
          message = error.description;
        } else if (error && typeof error.message === 'string') {
          message = error.message;
        }

        theme.showQuickPopup(message, submitBtn || form, 'error');
      });
    }
  };

  window.customElements.define('package-product-form', PackageProductForm);
})();
